import { useEffect } from "react";
import { api } from "../api/endpoints";
import type { AlertEvent, BlinkStatus, Camera, Drone, PanicButton } from "../api/types";
import { useResource } from "../lib/useResource";
import { parseTs } from "../lib/time";
import { usePageHidden } from "../hud/motion";
import { useRealtime, useRealtimeMessages } from "../realtime/RealtimeProvider";

// Everything the home scene shows, kept live: REST once (and again after each
// realtime reconnect), then realtime messages. Drone telemetry is not pushed
// on /ws, so drones are polled while the page is visible; cameras refresh on
// the server's 60 s Blink cadence (which also renews thumbnail media tokens).

const DRONE_POLL_MS = 10_000;
const CAMERA_REFRESH_MS = 60_000;
export const LOW_BATTERY_PCT = 20;

function upsert<T extends { id: string }>(list: T[] | undefined, item: T): T[] | undefined {
  if (!list) return list;
  const i = list.findIndex((x) => x.id === item.id);
  if (i === -1) return [...list, item];
  const next = list.slice();
  next[i] = item;
  return next;
}

function mergeEvent(prev: AlertEvent[] | undefined, e: AlertEvent): AlertEvent[] | undefined {
  if (!prev) return prev;
  const i = prev.findIndex((x) => x.id === e.id);
  const next = i === -1 ? [e, ...prev] : prev.map((x) => (x.id === e.id ? e : x));
  return next.sort((a, b) => (parseTs(b.created_at) ?? 0) - (parseTs(a.created_at) ?? 0));
}

export function useSceneData(withDrones: boolean) {
  const { conn } = useRealtime();
  const hidden = usePageHidden();

  const blink = useResource<BlinkStatus>((s) => api.blinkStatus(s), [conn.epoch]);
  const connected = blink.data?.status === "connected";
  const cams = useResource<Camera[] | null>((s) => (connected ? api.cameras(s) : Promise.resolve(null)), [connected, conn.epoch]);
  const buttons = useResource<PanicButton[]>((s) => api.buttons(s), [conn.epoch]);
  const drones = useResource<Drone[] | null>((s) => (withDrones ? api.drones(s) : Promise.resolve(null)), [withDrones, conn.epoch]);
  const events = useResource<AlertEvent[]>((s) => api.events({ limit: 50 }, s), [conn.epoch]);

  useRealtimeMessages((m) => {
    if (m.type === "button") buttons.setData((l) => upsert(l, m.button));
    if (m.type === "drone" && withDrones) drones.setData((l) => (l ? upsert(l, m.drone) : l));
    if (m.type === "blink") blink.setData(m.status);
    if (m.type === "event" || m.type === "event_ack") events.setData((l) => mergeEvent(l, m.event));
  });

  const setDrones = drones.setData;
  useEffect(() => {
    if (hidden || !withDrones) return;
    let live = true;
    const t = setInterval(() => {
      api
        .drones()
        .then((list) => {
          if (live) setDrones(list);
        })
        .catch(() => undefined);
    }, DRONE_POLL_MS);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [hidden, withDrones, setDrones]);

  const reloadCams = cams.reload;
  useEffect(() => {
    if (!connected || hidden) return;
    const t = setInterval(reloadCams, CAMERA_REFRESH_MS);
    return () => clearInterval(t);
  }, [connected, hidden, reloadCams]);

  return { blink, cams, buttons, drones, events };
}

export type SceneData = ReturnType<typeof useSceneData>;
